import { ObjectState } from './objectstate'
import { ArrayContext, MODE_REPLACE } from './context'
import { ANSWERED } from '../../config'
import debug from '../debug'

const { objectId } = require('../objectid')
const { Scope } = require('yatte')

export class ArrayState extends ArrayContext {
  constructor (
    name, typeName, keyName = undefined, parentContext = undefined, typeDict = undefined, sourceArray = undefined,
  ) {
    if (!(parentContext instanceof ObjectState)) {
      throw new Error('parentContext must be an instance of ObjectState')
    }
    super(name, typeName, keyName, parentContext, typeDict)
    this.mutable = parentContext.mutable
    if (sourceArray) {
      this.initSource(sourceArray)
    }
    // this.reset()
  }

  _createObjectState (varName, typeName, evalOnly = false) {
    return evalOnly
      ? this._createObjectContext(varName, typeName, evalOnly) // call ArrayContext's version to create an ObjectContext()
      : new ObjectState(varName, typeName, this)
  }

  // reset () {
  //   this.dirty = false
  // }

  resetSource () {
    this.elements.forEach(element => element.resetSource())
    this.source = undefined
  }

  initVirtuals () {
    super.initVirtuals()
    if (this.source) {
      // child initVirtuals (above) can affect the source objects, so rebuild the source array
      if (this.elements.length > 0 || this.source.length !== this.elements.length) {
        this.source = this.elements.map(element => element.source)
        // this.dirty = true
      }
    }
  }

  /**
   * Applies a potentially new sourceArray to this context's scope. A scope is treated immutably,
   * so if the scope's value changes, a new scope will be created rather than modifying the existing one.
   * It is assumed that sourceArray already fits the type -- values are not coerced into expected types.
   * Recursive.
   *
   * THIS FUNCTION IS MEANT TO BE CALLED WHILE RECURSING TOP-TO-BOTTOM; SPECIFICALLY, IT IS CALLED **AFTER**
   * MUTATEVARDATA BUBBLES UP TO REACH THE TOP OF THE DATA MODEL (but before MutateVarData returns, to be clear).
   * THIS GETS CALLED TO PUSH NEW DATA BACK DOWN THROUGH THE DATA STATE, FROM TOP TO BOTTOM.
   * IF YOU CALL IT *DURING* MUTATEVARDATA (i.e., before it has bubbled up to the top), IT WILL BREAK PROPER
   * UPDATING ON THAT PART OF THE DATA TREE LATER, WHEN REFRESHSCOPE IS CALLED (again) THEN!
   *
   * @param {object} sourceArray
   */
  refreshScope (sourceArray) {
    if (this.source) throw new Error('refreshScope called prior to scope initialization') // debug
    if (sourceArray && sourceArray.some(item => item && item.__value)) {
      sourceArray = sourceArray.map(item => (item && item.__value) || item) // unwrap proxies to reach actual values
    }
    const oldArray = this.scope && this.scope.value
    let valueChange
    if (sourceArray) {
      valueChange = !oldArray || oldArray.length !== sourceArray.length
        || oldArray.some((oldItem, index) => oldItem !== sourceArray[index])
    } else {
      valueChange = (oldArray && oldArray.length > 0)
    }
    if (valueChange) { // implies that one or more elements in this list was/were mutated
      let toPush
      if (!sourceArray || !sourceArray.length || !this.hasVirtualDefs) {
        toPush = sourceArray
      } else if (sourceArray[0] && Object.getPrototypeOf(sourceArray[0]) !== Object.prototype) {
        // or we could/should possibly consider the less permissive alternative:
        // else if (sourceArray[0] && Object.getPrototypeOf(sourceArray[0]) === this.virtualDefs) {
        toPush = sourceArray
      } else {
        toPush = sourceArray.map(element => element && Object.assign(Object.create(this.virtualDefs), element))
      }
      this.scope = Scope.pushList(toPush, this.parentContext && this.parentContext.scopeGetter)
    }
    if (valueChange) {
      const moveIdx = this.elements.findIndex(childState => childState && typeof childState.__move_to === 'number')
      if (moveIdx >= 0) {
        let elemToMove = this.elements[moveIdx]
        const newIdx = elemToMove.__move_to
        delete elemToMove.__move_to
        elemToMove = null
        this.refreshElementPosition(moveIdx, newIdx)
      }
      // ensure the number of child contexts match the number of values in the list scope
      const childStates = this.elements.length
      const childValues = (this.scope && this.scope.value && this.scope.value.length) || 0
      const extra = childStates - childValues
      if (extra > 0) {
        console.log('Warning: refreshScope is discarding unexpected extra rows')
        this.elements.splice(childValues, extra)
      }
      // recursively refresh child states
      this.elements.forEach((element, index) => {
        const elemVal = this.scope.value[index]
        element.refreshScope(elemVal)
      })
    }
  }

  refreshElementPosition (index, newIndex) {
    if (newIndex === index) return // no-op
    let firstMod, lastMod
    // remove the element to be moved or deleted
    const elem = this.elements.splice(index, 1)
    if (newIndex >= 0) { // MOVE index to newIndex
      this.elements.splice(newIndex, 0, elem)
      firstMod = Math.min(index, newIndex)
      lastMod = Math.max(index, newIndex)
    } else { // newIndex < 0, which means DELETE index
      firstMod = index
      lastMod = this.elements.length - 1
    }
    // rename/renumber all elements whose index changed
    for (let i = firstMod; i <= lastMod; i++) {
      const thisElement = this.elements[i]
      thisElement.name = i
      // todo: is there anything else that needs to be updated here??
    }
  }

  getStatus () {
    if (this.elements.length === 0) {
      return ANSWERED.NONE
    }
    if (this.elements.length === 1) {
      return this.elements[0].getStatus()
    }
    for (let index = this.elements.length - 1; index >= 0; index--) {
      if (this.elements[index].getStatus() !== ANSWERED.FULL) {
        return ANSWERED.PART
      }
    }
    return ANSWERED.FULL
  }

  overlayData (array, doLink) {
    // overlay array on top the current scope, with elements of the array overwriting those of the current scope.
    // We're basically following this scheme: the result of the overlay will be a new array that is
    // (element-for-element) key-matched to the input array. For each element of the new array, if an element with
    // matching key was located in this existing ArrayState, then the output array's element will be the result of
    // overlaying that input array element on top of the current data in the key-matched existing ObjectState.
    // But if an element with matching key was NOT located in the existing ArrayState, then the resulting element
    // will basically match the input element exactly.
    if (!array || !Array.isArray(array)) throw new Error('Array overlay requires an array')
    // "array" may be data directly from a queried record (in which case its elements will most likely NOT have _links
    // set), or it may have been produced by applying some mapping to data from a queried record (in which case
    // its elements MAY already have a _link). Either way, this method makes sure the output array's elements
    // all have _link set appropriately.
    const varDef = this.parentContext.getVarDef(this.name)
    debug.assert(varDef.type === 'object', 'Unexpected: overlayData called on list of non-objects')
    const keyName = this.keyName || 'id$'
    const keyBasedMatching = (keyName !== 'id$') // for custom keys we won't need to assign _link
    const newArray = []
    for (let i = 0; i < array.length; i++) {
      const incomingElement = array[i]
      const incomingKeyValue = incomingElement[keyName]
      let newElement
      // look for matching ObjectState in this.elements
      const existingState = this.elements.find(es => (
          keyBasedMatching && incomingKeyValue && es.getValue(keyName) === incomingKeyValue
        ) || (
          incomingElement._link && (es.linkedKeyVal || es.getValue('_link')) === incomingElement._link
        )
      )
      if (existingState) {
        newElement = existingState.overlayData(incomingElement, doLink)
      } else {
        newElement = this._getTypedObject(incomingElement, false, true)
        if (!keyBasedMatching && incomingKeyValue && doLink && !newElement._link) {
          newElement._link = incomingKeyValue
        }
      }
      newArray.push(newElement)
    }
    // before returning, we must also ensure that this.elements is prepared (shaped correctly)
    // to receive the updated values (later)
    if (this.elements.length > newArray.length) {
      this.elements.splice(newArray.length, this.elements.length - newArray.length)
    } else {
      for (let i = this.elements.length; i < newArray.length; i++) {
        this.elements[i] = this.constructObjectState(i, newArray[i], false)
      }
    }
    this.resetSource()
    return newArray
  }

  // generator for recursive enumeration of data points currently linked to external queries
  * queryLinks () {
    // recurse to all child contexts (this.elements)
    if (this.elements) {
      for (const childContext of this.elements) {
        yield * childContext.queryLinks()
      }
    }
  }

  mutateVarData (index, element) {
    const existingArray = this.scope && this.scope.value
    let newArray
    if (existingArray) {
      // get current property to check for change
      const currentElement = existingArray[index]
      if (currentElement !== element) {
        // create new array value with modified element
        newArray = existingArray.slice(0, index)
        newArray.push(element, ...existingArray.slice(index + 1))
      }
    } else { // no existing array
      newArray = []
      newArray[index] = element
    }
    if (newArray) {
      // this.dirty = true
      if (newArray.length !== existingArray.length) {
        // what, if anything, to do here?  Indicates list item is being created during mutation instead of prior to it.
        if (newArray.length === existingArray.length + 1 && index === newArray.length) {
        }
        // need to ensure, once we reach refreshScope, that the right list value is there, and that proper recursion allows it to be applied to this list scope
        // debugger
      }
      // hoist state change to parent context
      if (this.parentContext) {
        this.parentContext.mutateVarData(this.name, newArray)
      }
      return newArray
    }
    // should probably NOT change data right away, but maybe capture new value in a "new data object" member,
    // and manage notifying everything that needs to be dirtied because of the change
    // maybe it propagates the changes up to the parent by itself?  When it gets to the top, it calls
    // out to ITS parent -- which is a react component? -- and notifies it of the change, which then calls setState?
    // The top-level one, serving as state for the knack, means the knack will need a shouldComponentUpdate
    // that can check if it's dirty or not.
  }

  addNewObject (sourceObj = null) {
    const newIndex = (this.scope && this.scope.value && this.scope.value.length) || 0
    if (isPrimitiveWrapper(sourceObj)) {
      this.mutateVarData(newIndex, sourceObj)
    } else {
      const newState = this.constructObjectState(newIndex, sourceObj, false)
      const newObj = newState.source
      newState.resetSource()
      this.elements[newIndex] = newState
      this.mutateVarData(newIndex, newObj)
    }
    return newIndex
  }

  constructObjectState (newIndex, sourceObject = undefined, id$Override) {
    // add a new ObjectState because that's where the new object's virtuals will be defined
    let newChildState = this.elements[newIndex]
    if (!newChildState) {
      newChildState = this._createObjectState(newIndex, this.typeDef.name)
      newChildState.initVirtuals()
      // this.elements[newIndex] = newChildState
    }
    if (sourceObject) {
      if (id$Override && ('id$' in sourceObject)) {
        const { id$, ...otherData } = sourceObject
        sourceObject = otherData
      }
      // RATHER THAN calling initSource on the individual item here, what if instead we called InitSource on the entire parent array state?
      // because with this, we end up creating an item in elements[] without creating an item in scope.value[] or scope.items[]
      // First, though, I have tried commenting out the assignment (above) to this.elements[newIndex] = ...
      newChildState.initSource(sourceObject, MODE_REPLACE)
      return newChildState // .source // but up in mutateVarData, we now need to accumulate all the list items appropriately...???
      //newChildState.initVirtuals()
      //newChildState.initScope()
      //newChildState.initSelections()
      //return newChildState.scope.value
    } // else
    const prototype = newChildState.hasVirtualDefs ? newChildState.virtualDefs : Object.prototype
    let newObj = Object.create(prototype)
    // if (sourceObject) {
    //   newObj = Object.assign(newObj, sourceObject)
    // }
    newObj.id$ = objectId()
    newChildState.initSource(newObj, MODE_REPLACE)
    return newChildState
  }

  addObjects (objectArray) {
    if (this.parentContext && objectArray && objectArray.length) {
      const existingArray = this.scope && this.scope.value
      const existingLen = existingArray.length
      const newArray = [...existingArray]
      objectArray.forEach((item, index) => {
        const newIdx = existingLen + index
        const newState = this.constructObjectState(newIdx, item, true)
        newArray[newIdx] = newState.source
        newState.resetSource()
        this.elements[newIdx] = newState
      })
      // hoist state change to parent context
      this.parentContext.mutateVarData(this.name, newArray)
    }
  }

  deleteObject (index) {
    // flag/hint the deleted ObjectState
    const childState = this.getChildContext(index)
    childState.__move_to = -1
    // now go ahead and just delete the data
    const existingArray = this.scope && this.scope.value
    let newArray
    if (index === 0 && existingArray.length === 1) {
      newArray = null
    } else {
      // create new array value with modified element
      newArray = existingArray.slice(0, index)
      newArray.push(...existingArray.slice(index + 1))
    }
    // this.dirty = true
    // hoist state change to parent context
    if (this.parentContext) {
      this.parentContext.mutateVarData(this.name, newArray)
    }
    return newArray
  }

  moveObject (index, newIndex) {
    if (index === newIndex) return // no-op
    // flag/hint the ObjectState that WILL need to move (don't move it yet!)
    const childState = this.getChildContext(index)
    childState.__move_to = newIndex
    // now go ahead and mutate the data
    const existingArray = this.scope && this.scope.value
    let newArray
    newArray = existingArray.slice(0, index)
    newArray.push(...existingArray.slice(index + 1))
    newArray.splice(newIndex, 0, existingArray[index])
    // this.dirty = true
    // hoist state change to parent context
    if (this.parentContext) {
      this.parentContext.mutateVarData(this.name, newArray)
    }
    return newArray
  }
}

function isPrimitiveWrapper (obj) {
  const value = (obj && obj.__value) || obj // look inside proxies
  if (typeof value === 'object') {
    const constructor = value && value.constructor
    return constructor && ['String', 'Number', 'Boolean'/* , 'BigInt' */].includes(constructor.name)
  }
  return false
}
